import type { EnvelopeRecipient } from '@/components/accounting/reports/envelope-sheet';
import type { Account, AccountFlag } from '@/types/models';
import { recipientAddress } from '@/utils/postal-address';

// Street-number sorting now lives in the shared account-sort util (it's used by
// the customers list too). Re-exported here so existing callers/tests that
// import from `@/utils/mailings-filter` keep working.
export { accountStreetNumber, compareByStreetNumber, streetSortKey } from '@/utils/account-sort';

/**
 * Synthetic tag — NOT a stored account_flag. Computed from `account.hasEmail`
 * (any attached person has a non-empty email). Lets the picker exclude owners
 * who already receive statements by email. Kept distinct from `AccountFlag` so
 * it can never accidentally be written to the account_flags table.
 */
export const HAS_EMAIL_TAG = 'HAS_EMAIL' as const;
export type FilterTag = AccountFlag | typeof HAS_EMAIL_TAG;

/** Does this account carry the given tag (real flag or the synthetic Has-Email)? */
export const accountHasTag = (a: Account, tag: FilterTag): boolean =>
	tag === HAS_EMAIL_TAG ? !!a.hasEmail : !!a.flags?.includes(tag);

/**
 * Pure domain logic for the bulk-mailings picker (issues #196 + #172):
 * deciding which accounts qualify, and turning a qualifying account into a
 * #10-envelope recipient block. Kept UI-free so it's unit-testable and shared
 * between the selector screen and any future server-side mail run.
 */

export type BalanceMode = 'owing' | 'all' | 'custom';

export interface MailingFilters {
	balanceMode: BalanceMode;
	/** Custom-mode bounds, in whole dollars (UI-friendly); undefined = no bound. */
	minDollars?: number;
	maxDollars?: number;
	nameContains: string;
	/** Exclude renter-occupied units (account flag "R"). */
	excludeRenters: boolean;
	/** When non-empty, keep ONLY accounts carrying at least one of these tags
	 *  (OR semantics — "show me every Lien or Demand-Letter account"). */
	includeTags?: FilterTag[];
	/** Drop any account carrying at least one of these tags. Includes the
	 *  synthetic HAS_EMAIL tag, so "exclude Has Email" mails only owners
	 *  without an email on file. */
	excludeTags?: FilterTag[];
}

/**
 * Does an account qualify for the current filter set? This is the core
 * business rule of the bulk-mailings picker — it decides who gets a
 * statement/envelope.
 *
 * Semantics:
 *   - balance "owing"  → only positive balances (the dominant collections case)
 *   - balance "custom" → inclusive min/max in dollars against `balanceCents`
 *   - name match       → case-insensitive substring across the owner name,
 *                        source-system name, account number, and unit address
 *                        (so "Delany" matches whether it's the owner or the LLC)
 *   - exclude renters  → drops accounts carrying the "R" flag
 */
export const accountMatchesFilters = (a: Account, f: MailingFilters): boolean => {
	// 0 when the API withheld the balance (reader without the `accounting` flag). The
	// mailings picker itself is flag-gated, so this is a type floor rather than a case
	// that reaches a user — and "no balance" filtering as zero excludes rather than mails.
	const bal = a.balanceCents ?? 0;
	if (f.balanceMode === 'owing' && bal <= 0) return false;
	if (f.balanceMode === 'custom') {
		if (f.minDollars != null && bal < Math.round(f.minDollars * 100)) return false;
		if (f.maxDollars != null && bal > Math.round(f.maxDollars * 100)) return false;
	}
	const q = f.nameContains.trim().toLowerCase();
	if (q) {
		const hay = [a.primaryOwnerName, a.externalName, a.accountNumber, a.address]
			.filter(Boolean).join(' ').toLowerCase();
		if (!hay.includes(q)) return false;
	}
	if (f.excludeRenters && a.flags?.includes('R')) return false;
	// Include-by-tag: OR semantics — qualify if the account has ANY selected tag.
	if (f.includeTags && f.includeTags.length > 0) {
		if (!f.includeTags.some((t) => accountHasTag(a, t))) return false;
	}
	// Exclude-by-tag: drop if the account has ANY selected tag.
	if (f.excludeTags && f.excludeTags.some((t) => accountHasTag(a, t))) return false;
	return true;
};

/**
 * Build the envelope return-address block from the organization. When a logo is
 * present it REPLACES the company-name line (the logo art already carries the
 * name — see issue #172), so we emit address lines only; without a logo the
 * name leads. City/state/zip is composed into one line per postal convention.
 */
export const buildReturnLines = (
	org: { name?: string; address?: string; city?: string; state?: string; zip?: string } | null | undefined,
	hasLogo: boolean,
): string[] => {
	const cityStateZip = [[org?.city, org?.state].filter(Boolean).join(', '), org?.zip]
		.filter(Boolean).join(' ').trim();
	const addr = [org?.address, cityStateZip].filter((l): l is string => !!l && l.length > 0);
	return hasLogo ? addr : [org?.name, ...addr].filter((l): l is string => !!l && l.length > 0);
};

/** Build a #10-envelope recipient block from an account's mailing (or unit) address. */
export const recipientFor = (a: Account): EnvelopeRecipient => {
	// A company-owned account has no person, so `primaryOwnerName` is blank —
	// fall back to the account's own company name before the code (#424).
	const name = a.primaryOwnerName?.trim() || a.companyName?.trim()
		|| a.externalName?.trim() || a.accountNumber || '—';
	// Shared with the account page's on-screen mailing block so the address a
	// user sees is the address the envelope prints.
	const { coLine, addressLines } = recipientAddress(a);
	return {
		// Only print a company line when it ISN'T already the recipient name —
		// otherwise a company-owned account with no person prints its name twice.
		companyName: [a.companyName, a.primaryOwnerCompanyName]
			.map((s) => s?.trim())
			.find((s) => !!s && s !== name) || undefined,
		name,
		coLine,
		addressLines,
	};
};
